import {
  ANY_NATIONAL_NUMBER_PATTERN,
  MIN_NATIONAL_NUMBER_LENGTH,
  MOBILE_PHONE_NUMBER_RULES,
  OTHER_COUNTRIES_MAX_NATIONAL_LENGTH,
  PHP_NUMERIC_PREFIX_PATTERN,
} from '@packages/utils/constants/mobile-phone-number.ts'

const toCountryCodeDigits = (countryCode: number | string): string => String(countryCode).replace(/\D/g, '').replace(/^0+/, '')

const stripInternationalPrefix = (countryCodeDigits: string, typedNumber: string): string => {
  const digits = typedNumber.replace(/\D/g, '')

  if (typedNumber.trim().startsWith(`+${countryCodeDigits}`)) {
    return digits.slice(countryCodeDigits.length)
  }

  if (digits.startsWith(`00${countryCodeDigits}`)) {
    return digits.slice(countryCodeDigits.length + 2)
  }

  return digits
}

const stripTrunkZero = (countryCodeDigits: string, digits: string): string => {
  return MOBILE_PHONE_NUMBER_RULES[countryCodeDigits]?.hasTrunkZero
    ? digits.replace(/^0+/, '')
    : digits
}

const isMobileNumberOfCountry = (countryCodeDigits: string, nationalNumber: string): boolean => {
  const mobilePattern = MOBILE_PHONE_NUMBER_RULES[countryCodeDigits]?.mobilePattern ?? ANY_NATIONAL_NUMBER_PATTERN

  return mobilePattern.test(nationalNumber)
}

const toNationalMobileNumber = (countryCode: number | string, typedNumber: string): string => {
  const countryCodeDigits = toCountryCodeDigits(countryCode)

  return stripTrunkZero(countryCodeDigits, stripInternationalPrefix(countryCodeDigits, typedNumber))
}

/**
 * A country code typed without + or 00, also behind a stray zero ("46701234567", "046701234567"),
 * is taken off when the whole is not a mobile number and what is left is one, or the whole is
 * longer than any national number of the country and what is left is not.
 */
const stripBareCountryCode = (countryCodeDigits: string, nationalNumber: string): string => {
  const rule = MOBILE_PHONE_NUMBER_RULES[countryCodeDigits]
  const maxNationalLength = rule?.maxNationalLength ?? OTHER_COUNTRIES_MAX_NATIONAL_LENGTH[countryCodeDigits]

  if (!maxNationalLength || rule?.mobilePattern.test(nationalNumber)) {
    return nationalNumber
  }

  const isLandlineBehindTheCountryCode = (withoutCountryCode: string): boolean =>
    nationalNumber.length > maxNationalLength &&
    withoutCountryCode.length >= MIN_NATIONAL_NUMBER_LENGTH &&
    withoutCountryCode.length <= maxNationalLength

  const strippedNumber = [countryCodeDigits, `0${countryCodeDigits}`]
    .filter(prefix => nationalNumber.startsWith(prefix))
    .map(prefix => stripTrunkZero(countryCodeDigits, nationalNumber.slice(prefix.length)))
    .find(withoutCountryCode => rule?.mobilePattern.test(withoutCountryCode) || isLandlineBehindTheCountryCode(withoutCountryCode))

  return strippedNumber ?? nationalNumber
}

const toStoredNationalNumber = (countryCode: number | string, typedNumber: string): string => {
  const countryCodeDigits = toCountryCodeDigits(countryCode)

  return stripBareCountryCode(countryCodeDigits, toNationalMobileNumber(countryCodeDigits, typedNumber))
}

/**
 * The number as ad platforms hash it for matching: the country code and the national number as digits,
 * without + or trunk zero. A number typed with another country code than the given one, after + or 00,
 * keeps the country code it was typed with.
 */
const toInternationalPhoneDigits = (countryCode: number | string, typedNumber: string): string => {
  const countryCodeDigits = toCountryCodeDigits(countryCode)
  const typedInternationalNumber = typedNumber.trim().replace(/^00/, '+')

  if (typedInternationalNumber.startsWith('+') && !typedInternationalNumber.startsWith(`+${countryCodeDigits}`)) {
    return typedInternationalNumber.replace(/\D/g, '')
  }

  return `${countryCodeDigits}${toStoredNationalNumber(countryCodeDigits, typedNumber)}`
}

const isValidMobileNumber = (countryCode: number | string, typedNumber: string): boolean => {
  const countryCodeDigits = toCountryCodeDigits(countryCode)

  return isMobileNumberOfCountry(countryCodeDigits, toNationalMobileNumber(countryCodeDigits, typedNumber))
}

const isPlausiblePhoneNumber = (countryCode: number | string, typedNumber: string): boolean => {
  return ANY_NATIONAL_NUMBER_PATTERN.test(toStoredNationalNumber(countryCode, typedNumber))
}

const toPhpIntString = (value: number | string | null | undefined): string => {
  const numericPrefix = String(value ?? '').match(PHP_NUMERIC_PREFIX_PATTERN)?.[0] ?? '0'

  return String(Math.trunc(Number(numericPrefix)))
}

/**
 * Whether the booking backend accepts a booker's or traveller's phone number. Its request reads the
 * number as digits only, so "+46 70…" is checked as a country code typed without +, and the
 * country code through PHP's (int) cast. A number without digits is not plausible here; the
 * backend takes it as no number.
 */
const isPlausibleBookingPhoneNumber = (
  countryCode: number | string | null | undefined,
  phone: string | null | undefined
): boolean => {
  const digits = (phone ?? '').replace(/\D/g, '')

  return digits !== '' && isPlausiblePhoneNumber(toPhpIntString(countryCode), digits)
}

export {
  toNationalMobileNumber,
  toInternationalPhoneDigits,
  isValidMobileNumber,
  isPlausibleBookingPhoneNumber,
}
